import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import './Login.css';

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({ email, password });

    if (authError || !authData.user) {
      setError('Invalid email or password.');
      setLoading(false);
      return;
    }

    const { data: profile, error: profileError } = await supabase
      .from('users')
      .select('*')
      .eq('auth_user_id', authData.user.id)
      .maybeSingle();

    if (profileError || !profile) {
      setError('Your login was accepted but no Vera profile is linked to it. Contact your administrator.');
      await supabase.auth.signOut();
      setLoading(false);
      return;
    }

    if (!profile.is_active) {
      setError('This account has been deactivated.');
      await supabase.auth.signOut();
      setLoading(false);
      return;
    }

    onLogin(profile);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage('');

    await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    // Always show the same message, whether or not the email matched an
    // account — avoids revealing which email addresses have Vera accounts.
    setForgotMessage('If an account exists for that email address, a password reset link has been sent.');
    setForgotLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-bg">
        <div className="login-grid" />
      </div>

      <div className="login-container">
        <div className="login-brand">
          <img src="/bramley-logo.svg" alt="Bramley Engineering" className="login-logo-img" />
        </div>

        <div className="login-card">
          <div className="login-card-header">
            <img src="/vera-logo-header.svg" alt="Vera QMS" className="login-card-vera-logo" />
            <p className="login-subtitle">Quality Management System</p>
          </div>

          {mode === 'login' && (
            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">EMAIL ADDRESS</label>
                <input
                  className="form-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@bramleyengineering.co.uk"
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label className="form-label">PASSWORD</label>
                <input
                  className="form-input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              {error && (
                <div className="login-error">
                  <span className="error-icon">!</span>
                  {error}
                </div>
              )}

              <button
                className={`login-btn ${loading ? 'loading' : ''}`}
                type="submit"
                disabled={loading}
              >
                {loading ? 'SIGNING IN...' : 'SIGN IN TO VERA'}
              </button>

              <button
                type="button"
                onClick={() => { setMode('forgot'); setForgotEmail(email); setForgotMessage(''); }}
                style={{ background: 'none', border: 'none', color: '#ccc', marginTop: '0.75rem', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}
              >
                Forgotten your password?
              </button>
            </form>
          )}

          {mode === 'forgot' && (
            <form className="login-form" onSubmit={handleForgotSubmit}>
              <div className="form-group">
                <label className="form-label">EMAIL ADDRESS</label>
                <input
                  className="form-input"
                  type="email"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  placeholder="your@bramleyengineering.co.uk"
                  required
                  autoFocus
                />
              </div>

              {forgotMessage && (
                <div className="login-error" style={{ background: '#1e3a1e', color: '#8fd98f' }}>
                  {forgotMessage}
                </div>
              )}

              <button
                className={`login-btn ${forgotLoading ? 'loading' : ''}`}
                type="submit"
                disabled={forgotLoading}
              >
                {forgotLoading ? 'SENDING...' : 'SEND RESET LINK'}
              </button>

              <button
                type="button"
                onClick={() => { setMode('login'); setForgotMessage(''); }}
                style={{ background: 'none', border: 'none', color: '#ccc', marginTop: '0.75rem', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}
              >
                Back to sign in
              </button>
            </form>
          )}

          <div className="login-footer">
            <span className="login-version">Vera v1.0 — Bramley Engineering</span>
          </div>
        </div>
      </div>
    </div>
  );
}
